import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaTimes, FaFolderOpen, FaUpload, FaTrash, FaCopy, FaEnvelope, FaCheck, FaFilePdf, FaSync, FaDownload } from "react-icons/fa";
import { API_BASE } from "../../../api/config";
import { SCHREIBEN_API, api, blobDownload, STATUS_CLS, STATUS_LABEL } from "./schreibenUtils";
import UmsatzEmpfaenger from "./UmsatzEmpfaenger";
import "./UmsatzEmpfaenger.css";

const SCHRITTE = ["Auswahl", "Text", "Empfänger", "Anlagen & PDF", "Vorgang"];
const QUELLEN = [["mietvertrag", "Mietverträge"], ["vertrag", "Verträge"], ["versicherung", "Versicherungen"],
                 ["darlehen", "Darlehen/Bank"], ["adresse", "Adressen"], ["umsatz", "Aus Umsätzen (Serienbrief)"],
                 ["freitext", "Freitext"]];
const fmtKB = (b) => (b ? `${Math.round(b / 1024)} KB` : "");

/* Schreiben-Vorgang (Welle 90.2) — Aufbau wie der Kündigungs-Drawer:
   Kopf mit Status-Badge, Pfeil-Stepper, 5 Schritte inkl. Vorgangs-Sicht
   (Status, zugehörige Dokumente, Vorgangsordner, Schließen). */
export default function SchreibenAssistent({ vorgangId, onClose, onChanged, onProzess }) {
  const [id, setId] = useState(vorgangId || null);
  const [schritt, setSchritt] = useState(0);
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState("");

  // Stammdaten
  const [themen, setThemen] = useState([]);
  const [absender, setAbsender] = useState([]);
  const [objekte, setObjekte] = useState([]);
  const [themaId, setThemaId] = useState("");
  const [absKey, setAbsKey] = useState("");
  const [objektId, setObjektId] = useState("");
  // Text
  const [bausteinIds, setBausteinIds] = useState([]);
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [vorschau, setVorschau] = useState(null);
  // Empfänger
  const [empfQuelle, setEmpfQuelle] = useState("mietvertrag");
  const [empfListen, setEmpfListen] = useState({});
  const [empfFilter, setEmpfFilter] = useState("");
  const [auswahl, setAuswahl] = useState([]);
  const [empfFrei, setEmpfFrei] = useState("");
  const [empfMail, setEmpfMail] = useState("");
  // Vorgang / Ergebnis
  const [vorgang, setVorgang] = useState(null);
  const [browser, setBrowser] = useState(null);
  const [mailBetreff, setMailBetreff] = useState("");
  const [mailText, setMailText] = useState("");
  const [dokumente, setDokumente] = useState([]);
  const [ordner, setOrdner] = useState(null);
  const [uploadRichtung, setUploadRichtung] = useState("eingehend");

  useEffect(() => {
    (async () => {
      try {
        const [t, a, o] = await Promise.all([api("/themen"), api("/absender"), api("/objekte")]);
        setThemen(t.themen || []);
        setAbsender(a.absender || []);
        setObjekte(o.objekte || []);
      } catch (e) { setFehler(String(e.message || e)); }
    })();
  }, []);

  const ladeVorgang = useCallback(async (vid) => {
    const v = await api(`/${vid}`);
    setVorgang(v);
    setThemaId(v.thema_id || "");
    setAbsKey(v.absender_suchbegriff || "");
    setObjektId(v.objekt_id || "");
    setBausteinIds(Array.isArray(v.bausteine) ? v.bausteine : []);
    setBetreff(v.betreff || "");
    setText(v.text || "");
    setAuswahl(Array.isArray(v.empfaenger) ? v.empfaenger : (v.empfaenger?.adressblock ? [v.empfaenger] : []));
    setMailBetreff(v.mail_betreff || v.betreff || "");
    setMailText(v.mail_text || "");
    return v;
  }, []);

  useEffect(() => {
    if (!vorgangId) return;
    (async () => {
      try {
        const v = await ladeVorgang(vorgangId);
        setSchritt(v.status !== "erfasst" ? 4 : 1);
      } catch (e) { setFehler(String(e.message || e)); }
    })();
  }, [vorgangId, ladeVorgang]);

  const thema = useMemo(() => themen.find((t) => String(t.id) === String(themaId)), [themen, themaId]);
  const bausteine = useMemo(() => (thema?.bausteine || []).filter((b) => b.aktiv !== 0), [thema]);
  const absAuswahl = absender.find((a) => a.suchbegriff === absKey);

  const speichern = (felder) => api(`/${id}`, "PUT", { felder });

  const vorschauLaden = useCallback(async (vid) => {
    try { setVorschau(await api(`/${vid}/vorschau`)); }
    catch (e) { setFehler(String(e.message || e)); }
  }, []);

  const vorgangDaten = useCallback(async (vid) => {
    try {
      const [d, o] = await Promise.all([api(`/${vid}/dokumente`), api(`/${vid}/ordner`)]);
      setDokumente(d.dokumente || []);
      setOrdner(o);
    } catch (e) { setFehler(String(e.message || e)); }
  }, []);
  useEffect(() => { if (schritt === 4 && id) vorgangDaten(id); }, [schritt, id, vorgangDaten]);

  // ── Schritt-Übergänge ────────────────────────────────────────────────
  const weiterVonAuswahl = async () => {
    setBusy(true); setFehler("");
    try {
      if (!id) {
        const v = await api("", "POST", {
          thema_id: themaId ? Number(themaId) : null,
          absender_suchbegriff: absKey || null,
          objekt_id: objektId || null,
        });
        setId(v.id);
        await ladeVorgang(v.id);
      } else {
        await speichern({ thema_id: themaId ? Number(themaId) : null,
                          absender_suchbegriff: absKey || null, objekt_id: objektId || null });
      }
      setSchritt(1);
      onChanged?.();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  /* Anklicken = sofort übernehmen (Lennard 14.09.: kein extra Knopf mehr).
     Reihenfolge im Dokument = Sortierung im Thema, nicht die Klickreihenfolge. */
  const bausteinUmschalten = (id) => {
    const neu = bausteinIds.includes(id) ? bausteinIds.filter((x) => x !== id) : [...bausteinIds, id];
    setBausteinIds(neu);
    const gewaehlt = bausteine.filter((b) => neu.includes(b.id));
    setText(gewaehlt.map((b) => (b.text || "").trim()).filter(Boolean).join("\n\n"));
    const betreffe = gewaehlt.map((b) => (b.betreff || "").trim()).filter(Boolean);
    if (betreffe.length) setBetreff(betreffe[0]);
  };

  const weiterVonText = async () => {
    setBusy(true); setFehler("");
    try {
      await speichern({ betreff, text, bausteine: bausteinIds });
      await vorschauLaden(id);
      setSchritt(2);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  // ── Empfänger ────────────────────────────────────────────────────────
  useEffect(() => {
    if (schritt !== 2 || empfQuelle === "freitext" || empfQuelle === "umsatz") return;
    const key = `${empfQuelle}|${objektId}`;
    if (empfListen[key]) return;
    (async () => {
      try {
        const d = await api(`/empfaenger/liste?quelle=${empfQuelle}&objekt_id=${encodeURIComponent(objektId || "")}`);
        setEmpfListen((p) => ({ ...p, [key]: d.eintraege || [] }));
      } catch (e) { setFehler(String(e.message || e)); }
    })();
  }, [schritt, empfQuelle, objektId, empfListen]);

  const empfKey = (e) => `${e.quelle}:${e.id ?? (e.adressblock || []).join("|")}`;
  const istGewaehlt = (e) => auswahl.some((a) => empfKey(a) === empfKey(e));
  const umschalten = (e) => setAuswahl((p) =>
    p.some((a) => empfKey(a) === empfKey(e)) ? p.filter((a) => empfKey(a) !== empfKey(e)) : [...p, e]);

  const freitextHinzu = () => {
    const block = empfFrei.split("\n").map((z) => z.trim()).filter(Boolean);
    if (!block.length) return;
    umschalten({ quelle: "freitext", adressblock: block, mail: empfMail.trim(), label: block[0] });
    setEmpfFrei(""); setEmpfMail("");
  };

  const weiterVonEmpfaenger = async () => {
    setBusy(true); setFehler("");
    try {
      if (!auswahl.length) throw new Error("Bitte mindestens einen Empfänger anhaken.");
      await speichern({ empfaenger: auswahl });
      await vorschauLaden(id);
      setSchritt(3);
      onChanged?.();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  // ── Anlagen + PDF ────────────────────────────────────────────────────
  const browse = async (pfad) => {
    try { setBrowser(await api(`/anlagen/browse?path=${encodeURIComponent(pfad || "/Holthey Immobilien")}`)); }
    catch (e) { setFehler(String(e.message || e)); }
  };
  const anlageAusDropbox = async (datei) => {
    const v = await ladeVorgang(id);
    await speichern({ anlagen: [...(v.anlagen || []), { quelle: "dropbox", pfad: datei.path, name: datei.name, groesse: datei.groesse }] });
    await ladeVorgang(id);
    setBrowser(null);
  };
  const anlageEntfernen = async (idx) => {
    await speichern({ anlagen: (vorgang?.anlagen || []).filter((_, i) => i !== idx) });
    await ladeVorgang(id);
  };
  const anlageHochladen = async (file) => {
    if (!file) return;
    setBusy(true); setFehler("");
    try {
      const fd = new FormData();
      fd.append("datei", file);
      const res = await fetch(`${SCHREIBEN_API}/${id}/anlage-upload`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload fehlgeschlagen");
      await ladeVorgang(id);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const pdf = async (nurVorschau) => {
    setBusy(true); setFehler(""); setMeldung("");
    try {
      const res = await fetch(`${SCHREIBEN_API}/${id}/pdf?nur_vorschau=${nurVorschau ? 1 : 0}`, { method: "POST" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const name = await blobDownload(res, "Schreiben.pdf");
      if (!nurVorschau) {
        const pfad = res.headers.get("X-Ablage-Pfad");
        setMeldung(pfad ? `„${name}" erzeugt — abgelegt im Vorgangsordner (${decodeURIComponent(pfad)})` : `„${name}" erzeugt.`);
        await speichern({ mail_betreff: mailBetreff || betreff, mail_text: mailText });
        await ladeVorgang(id);
        setSchritt(4);
        onChanged?.();
      }
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  // ── Vorgangs-Sicht ───────────────────────────────────────────────────
  const statusSetzen = async (st) => {
    setBusy(true); setFehler("");
    try { await api(`/${id}/status`, "POST", { status: st }); await ladeVorgang(id); await vorgangDaten(id); onChanged?.(); }
    catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const dokumentHochladen = async (file) => {
    if (!file) return;
    setBusy(true); setFehler("");
    try {
      const fd = new FormData();
      fd.append("datei", file);
      const res = await fetch(`${SCHREIBEN_API}/${id}/dokument-upload?richtung=${uploadRichtung}`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload fehlgeschlagen");
      await vorgangDaten(id);
      await ladeVorgang(id);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const emlLaden = async () => {
    setBusy(true); setFehler("");
    try {
      await speichern({ mail_betreff: mailBetreff || betreff, mail_text: mailText });
      const res = await fetch(`${SCHREIBEN_API}/${id}/eml`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || ".eml fehlgeschlagen");
      await blobDownload(res, "Entwurf.eml");
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const kopieren = (wert) => navigator.clipboard?.writeText(wert || "");

  const listeKey = `${empfQuelle}|${objektId}`;
  const status = vorgang?.status || "erfasst";

  return (
    <div className="me-drawer-overlay" onClick={onClose}>
      <div className="me-drawer vg-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Kopf wie beim Kündigungsvorgang */}
        <div className="me-drawer-header">
          <div>
            <h3>
              {vorgang?.thema_name || (thema ? thema.name : "Neuer Vorgang")}{" "}
              <span className={`me-badge ${STATUS_CLS[status] || "me-badge-none"}`}>{STATUS_LABEL[status] || status}</span>
            </h3>
            <span className="me-drawer-sub">
              {id ? `Vorgang #${id} · ` : ""}Absender {absKey || "—"}
              {objektId ? ` · Objekt ${objektId}` : ""} · {auswahl.length} Empfänger
            </span>
          </div>
          <button className="me-btn me-btn-icon" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="me-drawer-body">
          {/* Pfeil-Stepper wie Kündigung */}
          <div className="vg-stepper">
            {SCHRITTE.map((s, i) => (
              <div key={s} className={`vg-step ${i === schritt ? "aktiv" : i < schritt ? "fertig" : ""}`}
                   style={{ cursor: id || i === 0 ? "pointer" : "default" }}
                   onClick={() => (id || i === 0) && setSchritt(i)}>
                <span className="vg-step-nr">{i + 1}</span><span>{s}</span>
              </div>
            ))}
          </div>

          {fehler && <div className="me-status error">❌ {fehler}</div>}
          {meldung && <div className="me-status success">✅ {meldung}</div>}

          {/* ── 1 · Auswahl: Thema + Absender + Objekt ── */}
          {schritt === 0 && (
            <div className="me-drawer-section">
              <h4>Thema, Absender und Objekt</h4>
              <div className="vg-grid">
                <span>Thema (Grund)</span>
                <select className="vg-in" value={themaId} onChange={(e) => {
                  // Welle 109: Themen mit Ablauf (z. B. „Neuer Mieter") starten keinen Brief
                  const t = themen.find((x) => String(x.id) === e.target.value);
                  if (t?.prozess && onProzess) { onProzess(t.prozess); return; }
                  setThemaId(e.target.value);
                }}>
                  <option value="">– wählen –</option>
                  {themen.filter((t) => t.id != null).map((t) => <option key={t.id} value={t.id}>{t.name}{t.prozess ? " → Ablauf" : ""}</option>)}
                </select>
                <span>Absenderfirma</span>
                <select className="vg-in" value={absKey} onChange={(e) => setAbsKey(e.target.value)}>
                  <option value="">– wählen –</option>
                  {absender.map((a) => <option key={a.suchbegriff} value={a.suchbegriff}>{a.suchbegriff} · {a.name}</option>)}
                </select>
                <span>Objekt (optional)</span>
                <select className="vg-in" value={objektId} onChange={(e) => setObjektId(e.target.value)}>
                  <option value="">— kein Objektbezug —</option>
                  {objekte.map((o) => <option key={o.nr} value={o.nr}>{o.nr}{o.name ? ` – ${o.name}` : ""}</option>)}
                </select>
              </div>
              <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>
                Das Objekt filtert später die Empfänger vor (Mieter, Versorger, Versicherungen, Bank des Objekts).
              </div>
              {absAuswahl && (
                <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>
                  <b>{absAuswahl.name}</b><br />{absAuswahl.anschrift}
                  {absAuswahl.mail && <><br />{absAuswahl.mail}</>}
                  {absAuswahl.unterschrift && <><br />Unterschrift: {absAuswahl.unterschrift}</>}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="me-btn me-btn-primary" disabled={busy || !themaId || !absKey}
                        onClick={weiterVonAuswahl}>Weiter →</button>
              </div>
            </div>
          )}

          {/* ── 2 · Text: Bausteine mehrfach wählen + Vorschau ── */}
          {schritt === 1 && (
            <>
              <div className="me-drawer-section">
                <h4>Textbausteine — anklicken übernimmt den Text sofort (mehrere möglich)</h4>
                <div className="me-table-wrap">
                  <table className="me-table">
                    <tbody>
                      {bausteine.map((b) => (
                        <tr key={b.id} style={{ cursor: "pointer" }}
                            onClick={() => bausteinUmschalten(b.id)}>
                          <td style={{ width: 34 }}>
                            <input type="checkbox" readOnly checked={bausteinIds.includes(b.id)} />
                          </td>
                          <td><b>{b.titel || b.key}</b>
                            {b.betreff && <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 12 }}>Betreff: {b.betreff}</span>}
                          </td>
                        </tr>
                      ))}
                      {!bausteine.length && <tr><td style={{ color: "#94a3b8" }}>Keine Bausteine im Thema — im Zahnrad-Menü anlegen.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 6 }}>
                  {bausteinIds.length
                    ? `${bausteinIds.length} Baustein${bausteinIds.length === 1 ? "" : "e"} übernommen — der Text unten ist frei änderbar (ein weiterer Klick setzt ihn neu zusammen).`
                    : "Noch kein Baustein gewählt — du kannst den Text unten auch frei schreiben."}
                </div>
              </div>

              <div className="me-drawer-section">
                <h4>Betreff & Text</h4>
                <div className="vg-grid">
                  <span>Betreff (optional)</span>
                  <input className="vg-in" value={betreff} onChange={(e) => setBetreff(e.target.value)} />
                </div>
                <textarea rows={10} style={{ width: "100%", marginTop: 8 }} value={text}
                          onChange={(e) => setText(e.target.value)} />
                {vorschau?.platzhalter_verfuegbar?.length > 0 && (
                  <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>
                    Platzhalter: {vorschau.platzhalter_verfuegbar.map((p) => `{${p}}`).join(" ")}
                  </div>
                )}
              </div>

              <div className="me-drawer-section">
                <h4>Vorschau{" "}
                  <button className="me-btn me-btn-icon" title="Aktualisieren (speichert den Text)"
                          onClick={async () => { await speichern({ betreff, text, bausteine: bausteinIds }); await vorschauLaden(id); }}>
                    <FaSync />
                  </button>
                </h4>
                {vorschau ? (
                  <div style={{ border: "1px solid #334155", borderRadius: 8, padding: 10, fontSize: 13, color: "#cbd5e1" }}>
                    <b>{vorschau.betreff || "(ohne Betreff)"}</b>
                    <div style={{ whiteSpace: "pre-wrap", marginTop: 6, maxHeight: 220, overflow: "auto" }}>{vorschau.text_gerendert}</div>
                    {vorschau.platzhalter_fehlend?.length > 0 && (
                      <div style={{ color: "#fca5a5", marginTop: 6 }}>Fehlt noch: {vorschau.platzhalter_fehlend.join(", ")}</div>
                    )}
                  </div>
                ) : <span style={{ color: "#94a3b8" }}>Auf ↻ klicken für die Vorschau.</span>}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(0)}>← Zurück</button>
                <button className="me-btn me-btn-primary" disabled={busy} onClick={weiterVonText}>Weiter →</button>
              </div>
            </>
          )}

          {/* ── 3 · Empfänger: Quellen-Reiter + Tabelle mit Checkboxen ── */}
          {schritt === 2 && (
            <div className="me-drawer-section">
              <h4>Empfänger wählen <span style={{ color: "#a5b4fc", fontWeight: 400 }}>— {auswahl.length} gewählt</span></h4>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {QUELLEN.map(([k, l]) => (
                  <button key={k} className={`vg-chip ${empfQuelle === k ? "aktiv" : ""}`}
                          onClick={() => { setEmpfQuelle(k); setEmpfFilter(""); }}>{l}</button>
                ))}
              </div>
              {objektId && !["freitext", "adresse", "umsatz"].includes(empfQuelle) && (
                <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>
                  Vorauswahl: nur Einträge zu Objekt {objektId}.{" "}
                  <span style={{ color: "#a5b4fc", cursor: "pointer" }} onClick={() => setObjektId("")}>Alle anzeigen</span>
                </div>
              )}

              {empfQuelle === "umsatz" ? (
                <UmsatzEmpfaenger absender={absKey} empfKey={empfKey} istGewaehlt={istGewaehlt} umschalten={umschalten}
                                  setAuswahl={setAuswahl} onFehler={setFehler} />
              ) : empfQuelle === "freitext" ? (
                <div className="vg-grid">
                  <span>Adressblock</span>
                  <textarea className="vg-in" rows={4} placeholder={"Firma Beispiel GmbH\nMusterstraße 1\n21255 Tostedt"}
                            value={empfFrei} onChange={(e) => setEmpfFrei(e.target.value)} />
                  <span>E-Mail (optional)</span>
                  <input className="vg-in" value={empfMail} onChange={(e) => setEmpfMail(e.target.value)} />
                  <span></span>
                  <div><button className="me-btn me-btn-secondary me-btn-sm" disabled={!empfFrei.trim()}
                          onClick={freitextHinzu}>+ Zur Auswahl hinzufügen</button></div>
                </div>
              ) : (
                <>
                  <input className="me-filter-input" style={{ width: "100%", marginBottom: 6 }}
                         placeholder="Filtern… (optional)" value={empfFilter}
                         onChange={(e) => setEmpfFilter(e.target.value)} />
                  <div className="me-table-wrap" style={{ maxHeight: 340, overflow: "auto" }}>
                    <table className="me-table">
                      <tbody>
                        {(() => {
                          const liste = empfListen[listeKey] || [];
                          const f = empfFilter.trim().toLowerCase();
                          const sichtbar = f
                            ? liste.filter((t) => (t.label + " " + (t.adressblock || []).join(" ") + " " + (t.gruppe || "")).toLowerCase().includes(f))
                            : liste;
                          if (!empfListen[listeKey]) return <tr><td style={{ color: "#94a3b8" }}>Lade Liste…</td></tr>;
                          if (!sichtbar.length) return <tr><td style={{ color: "#94a3b8" }}>Keine Treffer.</td></tr>;
                          let letzte = null;
                          const zeilen = [];
                          sichtbar.forEach((t) => {
                            if (t.gruppe && t.gruppe !== letzte) {
                              zeilen.push(
                                <tr key={`g-${t.gruppe}`}><td colSpan={3}
                                    style={{ background: "#1e293b", color: "#94a3b8", fontSize: 12, padding: "4px 8px" }}>
                                  {t.gruppe}</td></tr>);
                              letzte = t.gruppe;
                            }
                            zeilen.push(
                              <tr key={empfKey(t)} style={{ cursor: "pointer" }} onClick={() => umschalten(t)}>
                                <td style={{ width: 34 }}>
                                  <input type="checkbox" readOnly checked={istGewaehlt(t)} />
                                </td>
                                <td style={{ whiteSpace: "nowrap" }}><b>{t.label}</b></td>
                                <td style={{ color: "#94a3b8", fontSize: 12 }}>{(t.adressblock || []).join(", ")}</td>
                              </tr>);
                          });
                          return zeilen;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {auswahl.length > 30 && (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <b>{auswahl.length} Empfänger gewählt</b>{" "}
                  <span style={{ color: "#a5b4fc", cursor: "pointer" }} onClick={() => setAuswahl([])}>alle entfernen</span>
                </div>
              )}
              {auswahl.length > 0 && auswahl.length <= 30 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {auswahl.map((a) => (
                    <span key={empfKey(a)} className="vg-chip aktiv" style={{ cursor: "pointer" }}
                          title="Klicken zum Entfernen" onClick={() => umschalten(a)}>
                      {a.label || (a.adressblock || [])[0]} ✕
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(1)}>← Zurück</button>
                <button className="me-btn me-btn-primary" disabled={busy || !auswahl.length}
                        onClick={weiterVonEmpfaenger}>Weiter ({auswahl.length}) →</button>
              </div>
            </div>
          )}

          {/* ── 4 · Anlagen & PDF ── */}
          {schritt === 3 && (
            <>
              <div className="me-drawer-section">
                <h4>Anlagen — werden in JEDE Empfänger-PDF direkt mit eingebaut</h4>
                {(vorgang?.anlagen || []).map((a, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                    <FaFilePdf style={{ color: "#f87171" }} />
                    <span style={{ flex: 1 }}>{a.name} <small style={{ color: "#64748b" }}>{fmtKB(a.groesse)}</small></span>
                    <button className="me-btn me-btn-icon me-btn-danger" onClick={() => anlageEntfernen(i)}><FaTrash /></button>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button className="me-btn me-btn-secondary me-btn-sm" onClick={() => browse("")}>
                    <FaFolderOpen /> Aus Dropbox wählen
                  </button>
                  <label className="me-btn me-btn-secondary me-btn-sm" style={{ cursor: "pointer" }}>
                    <FaUpload /> Hochladen (PDF)
                    <input type="file" accept="application/pdf" style={{ display: "none" }}
                           onChange={(e) => { anlageHochladen(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>
              </div>

              <div className="me-drawer-section">
                <h4>PDF erzeugen</h4>
                <p style={{ color: "#94a3b8", fontSize: 13, margin: "0 0 8px" }}>
                  Je gewähltem Empfänger ({auswahl.length}) entsteht eine eigene fertige PDF — Anlagen
                  direkt enthalten — und wird im Vorgangsordner abgelegt. Der Download bündelt alle Briefe.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="me-btn me-btn-secondary" disabled={busy} onClick={() => pdf(true)}>PDF-Vorschau</button>
                  <button className="me-btn me-btn-primary" disabled={busy} onClick={() => pdf(false)}>
                    PDF erzeugen + im Vorgangsordner ablegen
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(2)}>← Zurück</button>
              </div>
            </>
          )}

          {/* ── 5 · Vorgang: Status, Dokumente, Ordner, Schließen ── */}
          {schritt === 4 && (
            <>
              <div className="me-drawer-section">
                <h4>Status</h4>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {Object.entries(STATUS_LABEL).map(([k, l]) => (
                    <button key={k} className={`vg-chip ${status === k ? "aktiv" : ""}`} disabled={busy}
                            onClick={() => statusSetzen(k)}>{l}</button>
                  ))}
                </div>
              </div>

              <div className="me-drawer-section">
                <h4>E-Mail-Entwurf <small style={{ color: "#94a3b8", fontWeight: 400 }}>(es wird nichts versendet)</small></h4>
                <div className="vg-grid">
                  <span>Betreff</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input className="vg-in" style={{ flex: 1 }} value={mailBetreff} onChange={(e) => setMailBetreff(e.target.value)} />
                    <button className="me-btn me-btn-icon" title="Kopieren" onClick={() => kopieren(mailBetreff)}><FaCopy /></button>
                  </div>
                  <span>Text</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <textarea className="vg-in" rows={4} style={{ flex: 1 }} value={mailText} onChange={(e) => setMailText(e.target.value)} />
                    <button className="me-btn me-btn-icon" title="Kopieren" onClick={() => kopieren(mailText)}><FaCopy /></button>
                  </div>
                </div>
                <button className="me-btn me-btn-secondary me-btn-sm" style={{ marginTop: 6 }}
                        disabled={busy || status === "erfasst"} onClick={emlLaden}>
                  <FaEnvelope /> .eml herunterladen
                </button>
              </div>

              <div className="me-drawer-section">
                <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  Zugehörige Dokumente ({dokumente.length})
                  <span style={{ marginLeft: "auto", display: "flex", gap: 6, fontWeight: 400 }}>
                    <select className="vg-in" style={{ fontSize: 12 }} value={uploadRichtung}
                            onChange={(e) => setUploadRichtung(e.target.value)}>
                      <option value="eingehend">eingehend (Rückmeldung)</option>
                      <option value="ausgehend">ausgehend</option>
                    </select>
                    <label className="me-btn me-btn-primary me-btn-sm" style={{ cursor: "pointer" }}>
                      <FaUpload /> Hochladen
                      <input type="file" style={{ display: "none" }}
                             accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff,.webp,.eml,.docx,.xlsx"
                             onChange={(e) => { dokumentHochladen(e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  </span>
                </h4>
                <div className="me-table-wrap">
                  <table className="me-table">
                    <thead><tr><th>Datei</th><th>Richtung</th><th>Typ</th><th>Datum</th><th></th></tr></thead>
                    <tbody>
                      {dokumente.map((d) => (
                        <tr key={d.id}>
                          <td style={{ wordBreak: "break-all" }}>{d.dateiname}</td>
                          <td>{d.richtung}</td>
                          <td>{d.typ}</td>
                          <td>{String(d.eingegangen_am || d.versendet_am || d.erzeugt_am || "").slice(0, 10)}</td>
                          <td>
                            {d.dropbox_pfad && (
                              <a className="me-btn me-btn-icon" title="Herunterladen"
                                 href={`${API_BASE}/mietverwaltung/api/kuendigung/dokument/${d.id}/download`}
                                 target="_blank" rel="noreferrer"><FaDownload /></a>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!dokumente.length && <tr><td colSpan={5} style={{ color: "#94a3b8" }}>Noch nichts abgelegt.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="me-drawer-section">
                <h4>Vorgangsordner in Dropbox{" "}
                  <button className="me-btn me-btn-icon" title="Aktualisieren" onClick={() => vorgangDaten(id)}><FaSync /></button>
                </h4>
                {ordner ? (
                  <>
                    <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>{ordner.pfad}</div>
                    {(ordner.dateien || []).length ? (ordner.dateien || []).map((f) => (
                      <div key={f.pfad || f.name} style={{ padding: "2px 0", fontSize: 13 }}>
                        📄 {f.name} <small style={{ color: "#64748b" }}>{fmtKB(f.groesse)}</small>
                      </div>
                    )) : <span style={{ color: "#94a3b8" }}>Ordner ist noch leer — er entsteht mit der ersten Ablage.</span>}
                  </>
                ) : <span style={{ color: "#94a3b8" }}>Lade Ordnerinhalt…</span>}
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
                <button className="me-btn me-btn-secondary" onClick={() => setSchritt(3)}>← Anlagen & PDF</button>
                {status !== "abgeschlossen" && (
                  <button className="me-btn me-btn-primary" disabled={busy} onClick={() => statusSetzen("abgeschlossen")}>
                    <FaCheck /> Vorgang schließen
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Dropbox-Mini-Browser */}
        {browser && (
          <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
               onClick={() => setBrowser(null)}>
            <div className="me-drawer" style={{ maxWidth: 620, width: "92vw", height: "auto", maxHeight: "80vh", overflow: "auto", borderRadius: 12, padding: 16 }}
                 onClick={(e) => e.stopPropagation()}>
              <div className="me-drawer-header">
                <h3><FaFolderOpen /> Anlage wählen</h3>
                <button className="me-btn me-btn-icon" onClick={() => setBrowser(null)}><FaTimes /></button>
              </div>
              <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 8 }}>{browser.pfad}</div>
              {browser.pfad !== "/Holthey Immobilien" && (
                <div style={{ padding: "4px 0", cursor: "pointer" }}
                     onClick={() => browse(browser.pfad.split("/").slice(0, -1).join("/"))}>⬆️ ..</div>
              )}
              {browser.ordner.map((o) => (
                <div key={o.path} style={{ padding: "4px 0", cursor: "pointer" }} onClick={() => browse(o.path)}>
                  📁 {o.name}
                </div>
              ))}
              {browser.dateien.map((d) => (
                <div key={d.path} style={{ padding: "4px 0", cursor: "pointer", color: "#a5b4fc" }}
                     onClick={() => anlageAusDropbox(d)}>
                  <FaFilePdf style={{ color: "#f87171", marginRight: 4 }} />{d.name}
                  <span style={{ color: "#64748b", fontSize: 11 }}> ({fmtKB(d.groesse)})</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
